/**
 * Type-safe outside click detection with advanced element type support
 */
type RefTarget = HTMLElement | SVGElement | MathMLElement;
type ElementRef<T extends RefTarget = HTMLElement> = React.RefObject<T | null>;
type ClickTarget = EventTarget & (Node | null);

/**
 * Advanced outside click detector with enhanced type safety and element support
 * Supports HTML, SVG, and MathML elements with proper type constraints
 *
 * @template TElement - Element type extending supported ref targets
 * @param target - The event target from mouse/touch events
 * @param refs - Array of React refs pointing to potential container elements
 * @returns Type-safe boolean indicating if click occurred outside all refs
 *
 * @example
 * ```typescript
 * const modalRef = useRef<HTMLDivElement>(null);
 * const buttonRef = useRef<HTMLButtonElement>(null);
 *
 * const handleClick = (event: MouseEvent) => {
 *   if (isOutsideClick(event.target, [modalRef, buttonRef])) {
 *     closeModal();
 *   }
 * };
 * ```
 */
export const isOutsideClick = <TElement extends RefTarget = HTMLElement>(
  target: EventTarget | null,
  refs: readonly ElementRef<TElement>[],
): target is ClickTarget => {
  // If the target itself is null, it can't be inside anything.
  if (!target) {
    return true;
  }

  // Type guard to ensure target is a Node for contains() method
  if (!('nodeType' in target)) {
    return true;
  }

  // Check if for *every* ref, the target is *not* contained within its element.
  // If ref.current is null, it satisfies the condition (!ref.current evaluates to true).
  return refs.every(ref => !ref.current?.contains(target as Node));
};
